import React from 'react';
import { CreditCard, Calendar, Clock, AlertCircle, CheckCircle2, Crown, Zap, ShieldAlert, ArrowRight, Phone } from 'lucide-react';
import { motion } from 'motion/react';
import { SubscriptionInfo, SubscriptionStatus, SubscriptionPlan } from '../types';
import PricingPanel from './Pricing';

interface SubscriptionManagementProps {
  subscription: SubscriptionInfo;
  onSelectPlan: (plan: SubscriptionPlan) => void;
}

const SubscriptionManagement: React.FC<SubscriptionManagementProps> = ({ subscription, onSelectPlan }) => {
  const isTrial = subscription.plan === SubscriptionPlan.FREE_TRIAL;
  const isGrace = subscription.status === SubscriptionStatus.GRACE_PERIOD;
  const isExpired = subscription.status === SubscriptionStatus.EXPIRED;
  
  const getStatusColor = () => {
    switch(subscription.status) {
      case SubscriptionStatus.ACTIVE: return 'text-emerald-500 bg-emerald-500/10 border-emerald-500/20';
      case SubscriptionStatus.TRIAL: return 'text-amber-500 bg-amber-500/10 border-amber-500/20';
      case SubscriptionStatus.GRACE_PERIOD: return 'text-rose-500 bg-rose-500/10 border-rose-500/20';
      case SubscriptionStatus.EXPIRED: return 'text-slate-500 bg-slate-500/10 border-slate-500/20';
      default: return 'text-indigo-500 bg-indigo-500/10 border-indigo-500/20';
    }
  };

  const getStatusLabel = () => {
    switch(subscription.status) {
      case SubscriptionStatus.ACTIVE: return 'Assinatura Ativa';
      case SubscriptionStatus.TRIAL: return 'Período de Teste';
      case SubscriptionStatus.GRACE_PERIOD: return 'Acesso Restrito (Aguardando Pagamento)';
      case SubscriptionStatus.EXPIRED: return 'Assinatura Encerrada';
      default: return 'Status Desconhecido';
    }
  };

  const remainingDays = () => {
    const end = new Date(subscription.endDate);
    const now = new Date();
    const diff = end.getTime() - now.getTime();
    return Math.max(0, Math.ceil(diff / (1000 * 60 * 60 * 24)));
  };

  return (
    <div className="relative font-sans">
      {/* Overlay de Beta / Em Construção */}
      <div className="absolute inset-0 z-50 flex items-center justify-center pointer-events-none">
        <div className="sticky top-1/2 -translate-y-1/2 bg-white/80 dark:bg-slate-900/80 backdrop-blur-md px-6 sm:px-12 py-6 rounded-[2rem] border-2 border-indigo-500/20 shadow-2xl flex flex-col items-center gap-2 pointer-events-auto">
          <div className="w-12 h-12 bg-indigo-600 rounded-2xl flex items-center justify-center text-white shadow-lg shadow-indigo-600/20">
            <Zap size={24} />
          </div>
          <h2 className="text-xl font-black dark:text-white uppercase tracking-tight">Em Construção</h2>
          <p className="text-[10px] font-black text-indigo-500 uppercase tracking-[0.2em]">Beta Version</p>
          <p className="text-xs text-slate-500 dark:text-slate-400 font-medium max-w-[200px] text-center mt-2">
            A plataforma está em fase de testes aberta. Aproveite todos os recursos gratuitamente.
          </p>
        </div>
      </div>

      <div className="space-y-8 sm:space-y-12 animate-in fade-in duration-700 pb-20 blur-xl opacity-45 pointer-events-none select-none">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-6">
          <div>
            <h1 className="text-3xl font-black dark:text-white tracking-tight">Planos e Assinaturas</h1>
            <p className="text-[11px] font-black uppercase tracking-widest text-slate-500 mt-1">Status da Versão Beta.</p>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
          {/* Card de Status */}
          <div className="lg:col-span-2 space-y-8">
            <div className="bg-white dark:bg-slate-900 rounded-[2rem] sm:rounded-[3rem] border border-slate-200 dark:border-slate-800 shadow-xl overflow-hidden">
              <div className="p-5 sm:p-10">
                <div className="flex flex-col md:flex-row md:items-center justify-between gap-8 mb-10">
                  <div className="flex items-center gap-5">
                    <div className="w-16 h-16 rounded-[1.5rem] flex items-center justify-center bg-indigo-600 text-white">
                      <Zap size={32} />
                    </div>
                    <div>
                      <h3 className="text-[10px] font-black text-slate-400 uppercase tracking-[0.2em] mb-1">Status de Acesso</h3>
                      <p className="text-2xl font-black dark:text-white tracking-tight">Beta Tester Liberado</p>
                    </div>
                  </div>
                  <div className={`px-6 py-2 rounded-2xl border text-[10px] font-black uppercase tracking-[0.15em] flex items-center gap-2 text-emerald-500 bg-emerald-500/10 border-emerald-500/20`}>
                    <CheckCircle2 size={14} />
                    Acesso Gratuito (Versão Beta)
                  </div>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div className="bg-slate-50 dark:bg-slate-800/50 p-6 rounded-[2rem] border border-slate-100 dark:border-slate-800">
                    <div className="flex items-center gap-3 mb-3 text-slate-400 dark:text-slate-500">
                      <Calendar size={16} />
                      <span className="text-[9px] font-black uppercase tracking-widest">Início do Teste</span>
                    </div>
                    <p className="text-lg font-black dark:text-white">{new Date(subscription.startDate).toLocaleDateString('pt-BR')}</p>
                  </div>
                  
                  <div className="bg-slate-50 dark:bg-slate-800/50 p-6 rounded-[2rem] border border-slate-100 dark:border-slate-800">
                    <div className="flex items-center gap-3 mb-3 text-slate-400 dark:text-slate-500">
                      <Clock size={16} />
                      <span className="text-[9px] font-black uppercase tracking-widest">Status da Expirar</span>
                    </div>
                    <p className="text-lg font-black dark:text-white uppercase tracking-tight">Sem Prazo (Beta)</p>
                  </div>

                  <div className="bg-slate-50 dark:bg-slate-800/50 p-6 rounded-[2rem] border border-slate-100 dark:border-slate-800">
                    <div className="flex items-center gap-3 mb-3 text-slate-400 dark:text-slate-500">
                      <CreditCard size={16} />
                      <span className="text-[9px] font-black uppercase tracking-widest">Renovação</span>
                    </div>
                    <p className="text-lg font-black dark:text-white">Não se aplica</p>
                  </div>
                </div>

                <div className="mt-8 p-6 bg-indigo-50 dark:bg-indigo-950/20 rounded-[2rem] border border-indigo-100 dark:border-indigo-900/30 flex items-start gap-4">
                  <Zap className="text-indigo-500 shrink-0 mt-1" size={24} />
                  <div>
                    <h4 className="text-sm font-black text-indigo-900 dark:text-indigo-200 uppercase tracking-tight">Obrigado por testar o Allodos</h4>
                    <p className="text-[11px] text-indigo-700 dark:text-indigo-400 mt-1 leading-relaxed font-medium">
                      Durante a fase Beta, todos os recursos estão liberados para feedback e aprimoramento da ferramenta. Suas análises ajudarão a construir o futuro da psicologia clínica.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Resumo lateral ou Destaque */}
          <div className="lg:col-span-1">
            <div className="bg-indigo-600 rounded-[2rem] sm:rounded-[3rem] p-5 sm:p-10 text-white shadow-xl h-full flex flex-col justify-between overflow-hidden relative group">
              <div className="absolute top-0 right-0 p-5 sm:p-8 opacity-10 group-hover:scale-110 transition-transform duration-500">
                <Crown size={120} />
              </div>
              
              <div className="relative">
                <h3 className="text-2xl font-black tracking-tight mb-4">Membro Fundador Beta</h3>
                <ul className="space-y-4">
                  {[
                    'Prioridade no Suporte',
                    'Acesso a Novas IAs',
                    'Selo exclusivo de Beta Tester'
                  ].map((item, i) => (
                    <li key={i} className="flex items-center gap-3 text-xs font-bold">
                      <div className="w-5 h-5 bg-white/20 rounded-lg flex items-center justify-center"><CheckCircle2 size={12} /></div>
                      {item}
                    </li>
                  ))}
                </ul>
              </div>

              <div className="mt-12 w-full py-5 bg-white/10 text-white rounded-2xl text-[10px] font-black uppercase tracking-widest border border-white/20 text-center">
                Acesso Vitalício Beta
              </div>
            </div>
          </div>
        </div>

        <div className="my-12 flex items-center gap-4">
          <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800"></div>
          <span className="text-[10px] font-black text-slate-500 dark:text-slate-400 uppercase tracking-[0.3em]">Opções de Contratação</span>
          <div className="h-px flex-1 bg-slate-200 dark:bg-slate-800"></div>
        </div>

        <div className="bg-slate-50 dark:bg-slate-950/40 rounded-[4rem] p-2 border border-slate-100 dark:border-slate-900">
          <PricingPanel onSelectPlan={onSelectPlan} currentPlan={subscription.plan} />
        </div>
      </div>

      <div className="flex justify-center mt-6 pb-20 relative z-50">
        <button 
          onClick={() => window.open(`https://wa.me/5554981115362?text=${encodeURIComponent("Olá, quero cancelar minha assinatura")}`, '_blank')}
          className="px-5 sm:px-10 py-5 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-[2rem] text-[10px] font-black uppercase tracking-[0.2em] text-slate-500 hover:text-rose-500 hover:border-rose-500/50 transition-all flex items-center gap-3 shadow-xl hover:scale-105 active:scale-95"
        >
          <Phone size={14} /> Cancelar Assinatura
        </button>
      </div>
    </div>
  );
};

export default SubscriptionManagement;
